import { useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { observer } from 'mobx-react';
import {
  Table,
  Space,
  Typography,
  Alert,
  Select,
  Button,
  Tooltip,
  Skeleton,
  Row,
  Col,
  Tag,
} from 'antd';
import {
  AreaChartOutlined,
  ReloadOutlined,
  DollarCircleOutlined,
  WarningOutlined,
  CompressOutlined,
  ApartmentOutlined,
  SafetyOutlined,
  ApiOutlined,
  ThunderboltOutlined,
  RobotOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  ToolOutlined,
  HourglassOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill } from '@/components/StatusPill';
import { KpiTile } from '@/components/KpiTile';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { HelpHotspot } from '@/features/help/HelpHotspot';
import { MiniBar, StackedBar, LineSparkline, HBarChart } from './InsightsViz';
import type {
  TokenCostBucket,
  ContextOverloadBucket,
  CompactionBucket,
  RouterAccuracyBucket,
  SafetyFlakinessBucket,
  ToolOverflowBucket,
  A2ALatencyBucket,
  RunLatencyBucket,
  ErrorMixBucket,
  ToolPerformanceBucket,
  ThreadContextBucket,
  ConversationCostBucket,
  ConversationCostReport,
} from '@/api/insights';
import { spendCoverage } from '@/api/insights';

const { Text } = Typography;

// ── Cost cell that never shows a missing cost as $0 ─────────────────────────
// A bucket whose calls all lack a cost reads "no rate" (not "$0.0000"); a bucket
// with some missing shows its stored cost with a warning that it is a floor.

function BucketCost({ row, maxCost }: { row: TokenCostBucket; maxCost: number }) {
  const noRate = row.callsWithoutRate ?? 0;
  const asZero = row.callsStoredAsZero ?? 0;
  const missing = noRate + asZero;
  const usd = Number(row.estimatedCostUsd ?? 0);
  const why = [
    noRate > 0 ? `${noRate.toLocaleString()} with no rate on file when they ran` : null,
    asZero > 0 ? `${asZero.toLocaleString()} stored as $0.00 (older rows were rounded to whole cents)` : null,
  ].filter(Boolean).join('; ');

  if (missing > 0 && missing === row.callCount && usd === 0) {
    return (
      // "not priced", not "no rate": the model may have a rate TODAY that these calls predate.
      <Tooltip title={`None of these ${row.callCount.toLocaleString()} calls has a cost: ${why}. Past calls are not re-priced; new calls use the rate in Model pricing.`}>
        <Tag color="gold" style={{ marginInlineEnd: 0 }}>not priced</Tag>
      </Tooltip>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
      <MiniBar value={usd} max={maxCost} tone="amber" width={80} />
      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, minWidth: 70, textAlign: 'right' }}>
        ${usd.toFixed(4)}
      </span>
      {missing > 0 && (
        <Tooltip title={`At least this much: ${missing.toLocaleString()} of ${row.callCount.toLocaleString()} calls have no cost — ${why}.`}>
          <WarningOutlined style={{ color: colors.amber[600] }} aria-label="cost is a floor" />
        </Tooltip>
      )}
    </div>
  );
}

// Single-page dashboard surfacing all 7 insights backed by the AI log tables:
//   1. Token cost by agent × model    (PromptLog)
//   2. Context overload hotspots      (PromptLog.PercentOfContext)
//   3. Compaction effectiveness       (PromptLog.Compaction*)
//   4. Skill router accuracy          (ValidationRunRouterDecision)
//   5. Safety scenario flakiness      (ValidationRunResult IsSafety)
//   6. Tool overflow risk             (ToolCallingLog)
//   7. A2A consultation latency       (AiAgentConsultationLog)

export const InsightsPage = observer(function InsightsPage() {
  const { insightsStore: store, merchantContextStore: ctx } = useStore();
  const [searchParams] = useSearchParams();

  // The dashboard "Top pressures" rows link here with ?agentId=N to scope
  // the Tool overflow tile to one agent. Parse the query param + push it
  // into the store so the affected tile refetches with the filter.
  const agentIdFromQuery = useMemo(() => {
    const raw = searchParams.get('agentId');
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [searchParams]);

  useEffect(() => {
    store.setAgentFilter(agentIdFromQuery);
  }, [agentIdFromQuery, store]);

  // Resolve the filter's display name from the loaded tokenCost rows
  // (already populated by fetchAll). Falls back to "#N" when the agent
  // hasn't been seen in the window.
  const filterAgentName = useMemo(() => {
    if (agentIdFromQuery == null) return null;
    const row = store.tokenCost.find((b) => b.agentId === agentIdFromQuery);
    return row?.agentName ?? `#${agentIdFromQuery}`;
  }, [agentIdFromQuery, store.tokenCost]);

  useEffect(() => {
    if (ctx.selectedMerchantId != null) {
      void store.fetchAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.selectedMerchantId]);

  if (ctx.selectedMerchantId == null) {
    return (
      <SectionCard>
        <BrandedEmpty
          icon={<AreaChartOutlined />}
          title="Pick a merchant to begin"
          description="Insights are scoped to a single merchant. Use the merchant switcher above."
          size="lg"
        />
      </SectionCard>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHeader
        accent="violet"
        icon={<AreaChartOutlined />}
        title={
          <Space size={6} align="center">
            <span>Insights</span>
            <HelpHotspot topic="insights.overview" />
          </Space>
        }
        subtitle={`Cross-table analytics derived from the AI log tables — ${ctx.selectedMerchant?.title ?? ''}.`}
        actions={
          <Space>
            <Tooltip title="Time window for time-bound queries (1, 7, 30, 90 days).">
              <Select
                style={{ width: 130 }}
                value={store.days}
                onChange={(v) => store.setDays(v)}
                options={[
                  { value: 1, label: 'Last 24 h' },
                  { value: 7, label: 'Last 7 days' },
                  { value: 30, label: 'Last 30 days' },
                  { value: 90, label: 'Last 90 days' },
                ]}
              />
            </Tooltip>
            <Button icon={<ReloadOutlined />} onClick={() => void store.fetchAll()}>
              Refresh all
            </Button>
          </Space>
        }
      />

      {/* Headline KPIs */}
      <HeadlineKpis />

      {/* 1. Token cost */}
      <SectionCard
        title={
          <Space>
            <DollarCircleOutlined /> 1 · Token cost by agent × model
          </Space>
        }
        subtitle={`From ai.PromptLog. Sum of EstimatedCostUsd across the last ${store.days} days, grouped by (AssistantId, ModelName).`}
      >
        <TokenCostTable
          rows={store.tokenCost}
          loading={store.loading.tokenCost}
          error={store.errors.tokenCost}
        />
      </SectionCard>

      {/* 2. Context overload */}
      <SectionCard
        title={
          <Space>
            <WarningOutlined /> 2 · Context overload hotspots
          </Space>
        }
        subtitle="Severity counts derived from PromptLog.PercentOfContext + AlertSeverity. Critical means the prompt used >= 95% of the model's MaxContextTokens."
      >
        <ContextOverloadTable
          rows={store.contextOverload}
          loading={store.loading.contextOverload}
          error={store.errors.contextOverload}
        />
      </SectionCard>

      {/* 3. Compaction effectiveness */}
      <SectionCard
        title={
          <Space>
            <CompressOutlined /> 3 · Compaction effectiveness
          </Space>
        }
        subtitle="When ContextCompactor fires, how much does it save? Per-strategy aggregate from PromptLog.CompactionApplied=true rows."
      >
        <CompactionTable
          rows={store.compaction}
          loading={store.loading.compaction}
          error={store.errors.compaction}
        />
      </SectionCard>

      {/* 4. Skill router accuracy */}
      <SectionCard
        title={
          <Space>
            <ApartmentOutlined /> 4 · Skill router accuracy over time
          </Space>
        }
        subtitle="Daily Routed vs. Failed counts from aivalidation.ValidationRunRouterDecision."
      >
        <RouterAccuracyChart
          rows={store.routerAccuracy}
          loading={store.loading.routerAccuracy}
          error={store.errors.routerAccuracy}
        />
      </SectionCard>

      {/* 5. Safety flakiness */}
      <SectionCard
        title={
          <Space>
            <SafetyOutlined /> 5 · Safety scenario flakiness
          </Space>
        }
        subtitle={`[SAFETY]-tagged scenarios across all runs (min ${store.minSafetyRuns} runs) — sorted by lowest pass rate first.`}
      >
        <SafetyFlakinessTable
          rows={store.safetyFlakiness}
          loading={store.loading.safetyFlakiness}
          error={store.errors.safetyFlakiness}
        />
      </SectionCard>

      {/* 6. Tool overflow — anchored so the dashboard's "Top pressures →
          Highest avg output / call" rows can deep-link here with
          /insights?agentId=N#tool-overflow and the operator lands on this
          tile already filtered to that agent. The filter pill is the
          honest signal: it tells the operator "you're looking at one
          agent" + offers a one-click clear. */}
      <div id="tool-overflow">
        <SectionCard
          title={
            <Space>
              <ApiOutlined /> 6 · Tool overflow risk profile
              {filterAgentName && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '2px 10px',
                    borderRadius: 999,
                    background: colors.violet[100],
                    color: colors.violet[700],
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                >
                  Filtered: {filterAgentName}
                  <Link
                    to="/insights"
                    style={{ color: colors.violet[700], fontSize: 11, fontWeight: 600, marginLeft: 4 }}
                    aria-label="Clear agent filter"
                  >
                    Clear ✕
                  </Link>
                </span>
              )}
            </Space>
          }
          subtitle={
            filterAgentName
              ? `Top plugin methods by argument-char size for ${filterAgentName}. Helps answer "which plugins are causing this agent's high output tokens?"`
              : 'Top 20 plugin methods by argument-char size. Ranks tools most likely to trigger output-token overflow (the 2026-04-23 incident pattern).'
          }
        >
          <ToolOverflowTable
            rows={store.toolOverflow}
            loading={store.loading.toolOverflow}
            error={store.errors.toolOverflow}
          />
        </SectionCard>
      </div>

      {/* 7. A2A latency */}
      <SectionCard
        title={
          <Space>
            <RobotOutlined /> 7 · A2A consultation latency
          </Space>
        }
        subtitle="Median + P95 + max consultation duration per (primary, peer) pair from ai.AiAgentConsultationLog."
      >
        <A2ALatencyTable
          rows={store.a2aLatency}
          loading={store.loading.a2aLatency}
          error={store.errors.a2aLatency}
        />
      </SectionCard>

      {/* 8. Run latency percentiles */}
      <SectionCard
        title={
          <Space>
            <ClockCircleOutlined /> 8 · Run latency percentiles
          </Space>
        }
        subtitle="P50 / P95 / P99 / Max LLM run duration per (agent × model) from ai.PromptLog. Watch P95 — that's the user-perceived ceiling."
      >
        <RunLatencyTable
          rows={store.runLatency}
          loading={store.loading.runLatency}
          error={store.errors.runLatency}
        />
      </SectionCard>

      {/* 9. Error mix */}
      <SectionCard
        title={
          <Space>
            <ExclamationCircleOutlined /> 9 · Error mix
          </Space>
        }
        subtitle="Per (agent × error_kind) errors over the window. Combines PromptLog HTTP failures with ToolCallingLog ErrorKind values — surfaces rate-limit storms and content-filter spikes."
      >
        <ErrorMixTable
          rows={store.errorMix}
          loading={store.loading.errorMix}
          error={store.errors.errorMix}
        />
      </SectionCard>

      {/* 10. Tool performance */}
      <SectionCard
        title={
          <Space>
            <ToolOutlined /> 10 · Tool performance
          </Space>
        }
        subtitle="Per (plugin, method) latency P50 / P95 / P99 + error rate from ai.ToolCallingLog. Identifies the weakest links in your tool surface."
      >
        <ToolPerformanceTable
          rows={store.toolPerformance}
          loading={store.loading.toolPerformance}
          error={store.errors.toolPerformance}
        />
      </SectionCard>

      {/* 11. Thread context utilization */}
      <SectionCard
        title={
          <Space>
            <HourglassOutlined /> 11 · Conversations near the truncation cliff
          </Space>
        }
        subtitle="Top 25 threads by max context utilization. Conversations approaching 100% are at risk of losing earlier turns to compaction or hard truncation."
      >
        <ThreadContextTable
          rows={store.threadContext}
          loading={store.loading.threadContext}
          error={store.errors.threadContext}
        />
      </SectionCard>

      {/* 12. Conversation cost distribution */}
      <SectionCard
        title={
          <Space>
            <MessageOutlined /> 12 · Conversation cost distribution
          </Space>
        }
        subtitle="Top 25 threads by estimated USD cost, plus the long-tail distribution (median, P95, P99, max). Outliers usually mean a single conversation is being amortized across many turns."
      >
        <ConversationCostPanel
          report={store.conversationCost}
          loading={store.loading.conversationCost}
          error={store.errors.conversationCost}
        />
      </SectionCard>
    </div>
  );
});

// ── Headline KPIs row ───────────────────────────────────────────────────────

const HeadlineKpis = observer(function HeadlineKpis() {
  const { insightsStore: store } = useStore();
  const spend = spendCoverage(store.tokenCost);
  const criticalCalls = store.contextOverload.reduce((sum, b) => sum + b.criticalCount, 0);
  const compactedCalls = store.compaction.reduce((sum, b) => sum + b.appliedCount, 0);

  return (
    <Row gutter={[12, 12]}>
      <Col xs={24} sm={12} md={6}>
        <KpiTile
          label="Spend (est.)"
          // A call without a cost is never priced at $0: when any are missing, the figure is a floor and says so.
          value={`${spend.missing > 0 ? '≥ ' : ''}$${spend.usd.toFixed(2)}`}
          hint={
            spend.missing > 0
              ? `${spend.missing.toLocaleString()} of ${spend.calls.toLocaleString()} calls have no cost — real spend is higher`
              : `${spend.calls.toLocaleString()} LLM calls by agents`
          }
          icon={<DollarCircleOutlined />}
          accent="amber"
        />
      </Col>
      <Col xs={24} sm={12} md={6}>
        <KpiTile
          label="Critical context"
          value={criticalCalls}
          hint={criticalCalls === 0 ? 'No prompts ≥ 95% of max' : 'Prompts hitting Critical'}
          icon={<WarningOutlined />}
          accent={criticalCalls > 0 ? 'red' : 'green'}
        />
      </Col>
      <Col xs={24} sm={12} md={6}>
        <KpiTile
          label="Compactions"
          value={compactedCalls}
          hint="LLM calls where compaction fired"
          icon={<CompressOutlined />}
          accent="violet"
        />
      </Col>
      <Col xs={24} sm={12} md={6}>
        <KpiTile
          label="Models in use"
          value={new Set(store.tokenCost.map((b) => b.modelName)).size}
          hint="Distinct ModelName values"
          icon={<ThunderboltOutlined />}
          accent="ink"
        />
      </Col>
    </Row>
  );
});

// ── 1. Token cost table ─────────────────────────────────────────────────────

const TokenCostTable = observer(function TokenCostTable({
  rows,
  loading,
  error,
}: {
  rows: TokenCostBucket[];
  loading: boolean;
  error: string | null;
}) {
  const maxCost = Math.max(0, ...rows.map((r) => Number(r.estimatedCostUsd ?? 0)));
  const columns: ColumnsType<TokenCostBucket> = [
    {
      title: 'Agent',
      key: 'agent',
      render: (_text, row) =>
        row.agentName ? (
          <Text style={{ fontSize: 13 }}>
            {row.agentName}{' '}
            <span style={{ color: colors.slate[500], fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
              #{row.agentId}
            </span>
          </Text>
        ) : (
          <Text type="secondary">unattributed</Text>
        ),
    },
    {
      title: 'Model',
      dataIndex: 'modelName',
      key: 'modelName',
      width: 200,
      render: (m: string) => (
        <Text style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>{m ?? '—'}</Text>
      ),
    },
    {
      title: 'Calls',
      dataIndex: 'callCount',
      key: 'callCount',
      width: 90,
      align: 'right',
      sorter: (a, b) => a.callCount - b.callCount,
      render: (n: number) => n.toLocaleString(),
    },
    {
      title: 'Tokens (in / out / cached)',
      key: 'tokens',
      width: 240,
      render: (_t, row) => (
        <Text style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11.5 }}>
          {row.inputTokens.toLocaleString()} / {row.outputTokens.toLocaleString()}
          {row.cachedTokens > 0 && (
            <span style={{ color: colors.green[600] }}> · cached {row.cachedTokens.toLocaleString()}</span>
          )}
        </Text>
      ),
    },
    {
      title: 'Avg latency',
      dataIndex: 'avgDurationMs',
      key: 'avg',
      width: 110,
      align: 'right',
      sorter: (a, b) => a.avgDurationMs - b.avgDurationMs,
      render: (n: number) => `${n.toLocaleString()} ms`,
    },
    {
      title: 'Cost',
      key: 'cost',
      width: 220,
      align: 'right',
      sorter: (a, b) => Number(a.estimatedCostUsd) - Number(b.estimatedCostUsd),
      defaultSortOrder: 'descend' as const,
      render: (_t, row) => <BucketCost row={row} maxCost={maxCost} />,
    },
  ];

  return <DataTable rows={rows} columns={columns} loading={loading} error={error} rowKey={(r) => `${r.agentId}-${r.modelName}`} />;
});

// ── 2. Context overload table ───────────────────────────────────────────────

const ContextOverloadTable = observer(function ContextOverloadTable({
  rows,
  loading,
  error,
}: {
  rows: ContextOverloadBucket[];
  loading: boolean;
  error: string | null;
}) {
  const columns: ColumnsType<ContextOverloadBucket> = [
    {
      title: 'Agent',
      key: 'agent',
      render: (_t, row) =>
        row.agentName ? <span>{row.agentName}</span> : <Text type="secondary">unattributed</Text>,
    },
    {
      title: 'Model',
      dataIndex: 'modelName',
      key: 'modelName',
      width: 200,
      render: (m: string) => (
        <Text style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>{m ?? '—'}</Text>
      ),
    },
    {
      title: 'Severity mix',
      key: 'severity',
      width: 240,
      render: (_t, row) => (
        <StackedBar
          segments={[
            { value: row.criticalCount, color: colors.red[600], label: 'Critical' },
            { value: row.warningCount, color: colors.amber[600], label: 'Warning' },
            { value: row.healthyCount, color: colors.green[600], label: 'Healthy' },
          ]}
        />
      ),
    },
    {
      title: 'Critical',
      dataIndex: 'criticalCount',
      key: 'critical',
      width: 90,
      align: 'right',
      sorter: (a, b) => a.criticalCount - b.criticalCount,
      defaultSortOrder: 'descend' as const,
      render: (n: number) => (
        <span style={{ color: n > 0 ? colors.red[600] : 'inherit', fontWeight: n > 0 ? 600 : 400 }}>
          {n.toLocaleString()}
        </span>
      ),
    },
    {
      title: 'Warning',
      dataIndex: 'warningCount',
      key: 'warning',
      width: 90,
      align: 'right',
      sorter: (a, b) => a.warningCount - b.warningCount,
    },
    {
      title: 'Avg %',
      key: 'avgPct',
      width: 100,
      align: 'right',
      render: (_t, row) =>
        row.avgPercentOfContext != null ? `${Number(row.avgPercentOfContext).toFixed(1)}%` : '—',
    },
    {
      title: 'Max %',
      key: 'maxPct',
      width: 100,
      align: 'right',
      render: (_t, row) => {
        const v = row.maxPercentOfContext != null ? Number(row.maxPercentOfContext) : null;
        const tone = v == null ? 'inherit' : v >= 95 ? colors.red[600] : v >= 80 ? colors.amber[600] : 'inherit';
        return (
          <span style={{ color: tone, fontWeight: v != null && v >= 80 ? 600 : 400 }}>
            {v != null ? `${v.toFixed(1)}%` : '—'}
          </span>
        );
      },
    },
  ];

  return <DataTable rows={rows} columns={columns} loading={loading} error={error} rowKey={(r) => `${r.agentId}-${r.modelName}`} />;
});

// ── 3. Compaction table ─────────────────────────────────────────────────────

const CompactionTable = observer(function CompactionTable({
  rows,
  loading,
  error,
}: {
  rows: CompactionBucket[];
  loading: boolean;
  error: string | null;
}) {
  const columns: ColumnsType<CompactionBucket> = [
    {
      title: 'Strategy',
      dataIndex: 'strategyUsed',
      key: 'strategyUsed',
      render: (s: string) => <StatusPill tone="violet">{s}</StatusPill>,
    },
    {
      title: 'Times applied',
      dataIndex: 'appliedCount',
      key: 'appliedCount',
      width: 130,
      align: 'right',
      sorter: (a, b) => a.appliedCount - b.appliedCount,
    },
    {
      title: 'Tokens before',
      dataIndex: 'totalTokensBefore',
      key: 'before',
      width: 140,
      align: 'right',
      render: (n: number) => n.toLocaleString(),
    },
    {
      title: 'Tokens after',
      dataIndex: 'totalTokensAfter',
      key: 'after',
      width: 140,
      align: 'right',
      render: (n: number) => n.toLocaleString(),
    },
    {
      title: 'Avg reduction',
      dataIndex: 'avgTokenReductionPct',
      key: 'reduction',
      width: 200,
      align: 'right',
      render: (n: number) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
          <MiniBar value={Number(n)} max={100} tone="green" width={80} />
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>
            {Number(n).toFixed(1)}%
          </span>
        </div>
      ),
    },
    {
      title: 'Messages removed',
      dataIndex: 'totalMessagesRemoved',
      key: 'removed',
      width: 150,
      align: 'right',
      render: (n: number) => n.toLocaleString(),
    },
  ];

  return <DataTable rows={rows} columns={columns} loading={loading} error={error} rowKey={(r) => r.strategyUsed} />;
});

// ── 4. Router accuracy chart ────────────────────────────────────────────────

const RouterAccuracyChart = observer(function RouterAccuracyChart({
  rows,
  loading,
  error,
}: {
  rows: RouterAccuracyBucket[];
  loading: boolean;
  error: string | null;
}) {
  if (loading && rows.length === 0) return <Skeleton active paragraph={{ rows: 3 }} />;
  if (error) return <Alert type="error" message={error} showIcon closable />;
  if (rows.length === 0) return <BrandedEmpty title="No router decisions in window" size="sm" />;

  const totalRouted = rows.reduce((sum, r) => sum + r.routedCount, 0);
  const totalFailed = rows.reduce((sum, r) => sum + r.failedFallbackCount, 0);
  const totalNoSkills = rows.reduce((sum, r) => sum + r.noSkillsLinkedCount, 0);
  const passRate = totalRouted + totalFailed > 0
    ? (100 * totalRouted / (totalRouted + totalFailed)).toFixed(1)
    : '—';

  return (
    <div>
      <Row gutter={12} style={{ marginBottom: 14 }}>
        <Col span={6}>
          <SmallStat label="Routed" value={totalRouted.toLocaleString()} accent="green" />
        </Col>
        <Col span={6}>
          <SmallStat label="Failed fallback" value={totalFailed.toLocaleString()} accent="red" />
        </Col>
        <Col span={6}>
          <SmallStat label="No skills linked" value={totalNoSkills.toLocaleString()} accent="amber" />
        </Col>
        <Col span={6}>
          <SmallStat label="Routed pass rate" value={`${passRate}%`} accent="ink" />
        </Col>
      </Row>
      <div style={{ marginTop: 4 }}>
        <Text type="secondary" style={{ fontSize: 12, marginBottom: 6, display: 'block' }}>
          Daily Routed count
        </Text>
        <LineSparkline values={rows.map((r) => r.routedCount)} width={520} height={48} tone="green" />
        <Text type="secondary" style={{ fontSize: 12, marginTop: 12, marginBottom: 6, display: 'block' }}>
          Daily Failed-fallback count
        </Text>
        <LineSparkline values={rows.map((r) => r.failedFallbackCount)} width={520} height={48} tone="amber" />
      </div>
    </div>
  );
});

// ── 5. Safety flakiness table ───────────────────────────────────────────────

const SafetyFlakinessTable = observer(function SafetyFlakinessTable({
  rows,
  loading,
  error,
}: {
  rows: SafetyFlakinessBucket[];
  loading: boolean;
  error: string | null;
}) {
  const columns: ColumnsType<SafetyFlakinessBucket> = [
    {
      title: 'Scenario',
      dataIndex: 'scenarioId',
      key: 'scenarioId',
      render: (id: string) => (
        <Text style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5, fontWeight: 600 }}>
          {id}
        </Text>
      ),
    },
    {
      title: 'Agent',
      key: 'agent',
      width: 220,
      render: (_t, row) =>
        row.agentName ? row.agentName : <Text type="secondary">#{row.agentId}</Text>,
    },
    {
      title: 'Runs',
      dataIndex: 'runCount',
      key: 'runs',
      width: 80,
      align: 'right',
    },
    {
      title: 'Pass / Fail',
      key: 'passfail',
      width: 220,
      render: (_t, row) => (
        <StackedBar
          segments={[
            { value: row.passCount, color: colors.green[600], label: 'Pass' },
            { value: row.failCount, color: colors.red[600], label: 'Fail' },
          ]}
        />
      ),
    },
    {
      title: 'Pass rate',
      dataIndex: 'passRatePct',
      key: 'passRate',
      width: 110,
      align: 'right',
      sorter: (a, b) => a.passRatePct - b.passRatePct,
      defaultSortOrder: 'ascend' as const,
      render: (n: number) => {
        const tone = n < 80 ? colors.red[600] : n < 95 ? colors.amber[600] : colors.green[600];
        return <span style={{ color: tone, fontWeight: 600 }}>{n.toFixed(1)}%</span>;
      },
    },
    {
      title: 'Avg score',
      dataIndex: 'avgScore',
      key: 'avgScore',
      width: 100,
      align: 'right',
      render: (n: number) => `${Number(n).toFixed(1)}`,
    },
  ];

  return <DataTable rows={rows} columns={columns} loading={loading} error={error} rowKey={(r) => `${r.scenarioId}-${r.agentId}`} />;
});

// ── 6. Tool overflow table ──────────────────────────────────────────────────

const ToolOverflowTable = observer(function ToolOverflowTable({
  rows,
  loading,
  error,
}: {
  rows: ToolOverflowBucket[];
  loading: boolean;
  error: string | null;
}) {
  if (loading && rows.length === 0) return <Skeleton active paragraph={{ rows: 3 }} />;
  if (error) return <Alert type="error" message={error} showIcon closable />;
  if (rows.length === 0) return <BrandedEmpty title="No tool calls in window" size="sm" />;

  // Two side-by-side rankings: by argument size, by response size.
  const argRows = rows.slice(0, 10).map((r) => ({
    label: `${r.pluginName}.${r.methodName}`,
    value: r.maxArgChars,
    tone: r.maxArgChars > 30000 ? 'red' as const : r.maxArgChars > 10000 ? 'amber' as const : 'ink' as const,
    hint: `Median ${r.medianArgChars.toLocaleString()} · ${r.callCount} calls`,
  }));
  const respRows = rows.slice(0, 10).map((r) => ({
    label: `${r.pluginName}.${r.methodName}`,
    value: r.maxResponseChars,
    tone: r.maxResponseChars > 30000 ? 'red' as const : r.maxResponseChars > 10000 ? 'amber' as const : 'violet' as const,
  }));

  return (
    <Row gutter={20}>
      <Col span={12}>
        <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          Top by max argument chars (overflow risk)
        </Text>
        <HBarChart rows={argRows} unit="ch" />
      </Col>
      <Col span={12}>
        <Text strong style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
          Top by max response chars
        </Text>
        <HBarChart rows={respRows} unit="ch" />
      </Col>
    </Row>
  );
});

// ── 7. A2A latency table ────────────────────────────────────────────────────

const A2ALatencyTable = observer(function A2ALatencyTable({
  rows,
  loading,
  error,
}: {
  rows: A2ALatencyBucket[];
  loading: boolean;
  error: string | null;
}) {
  const columns: ColumnsType<A2ALatencyBucket> = [
    {
      title: 'Primary',
      dataIndex: 'primaryAgentName',
      key: 'primary',
      render: (n: string) => n ?? '—',
    },
    {
      title: 'Peer',
      dataIndex: 'peerAgentName',
      key: 'peer',
      render: (n: string) => (
        <Text style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5 }}>{n ?? '—'}</Text>
      ),
    },
    {
      title: 'Calls',
      dataIndex: 'callCount',
      key: 'calls',
      width: 80,
      align: 'right',
    },
    {
      title: 'Median',
      dataIndex: 'medianMs',
      key: 'median',
      width: 110,
      align: 'right',
      render: (n: number) => `${n.toLocaleString()} ms`,
    },
    {
      title: 'P95',
      dataIndex: 'p95Ms',
      key: 'p95',
      width: 110,
      align: 'right',
      sorter: (a, b) => a.p95Ms - b.p95Ms,
      defaultSortOrder: 'descend' as const,
      render: (n: number) => {
        const tone = n > 5000 ? colors.red[600] : n > 2000 ? colors.amber[600] : 'inherit';
        return <span style={{ color: tone, fontWeight: tone === 'inherit' ? 400 : 600 }}>{n.toLocaleString()} ms</span>;
      },
    },
    {
      title: 'Max',
      dataIndex: 'maxMs',
      key: 'max',
      width: 110,
      align: 'right',
      render: (n: number) => `${n.toLocaleString()} ms`,
    },
    {
      title: 'Timeouts',
      dataIndex: 'timeoutCount',
      key: 'timeouts',
      width: 100,
      align: 'right',
      render: (n: number) =>
        n > 0 ? <span style={{ color: colors.red[600], fontWeight: 600 }}>{n}</span> : 0,
    },
    {
      title: 'Errors',
      dataIndex: 'errorCount',
      key: 'errors',
      width: 90,
      align: 'right',
      render: (n: number) => (n > 0 ? <span style={{ color: colors.red[600] }}>{n}</span> : 0),
    },
  ];

  return <DataTable rows={rows} columns={columns} loading={loading} error={error} rowKey={(r) => `${r.primaryAgentId}-${r.peerAgentId}`} />;
});

// ── 8. Run latency table ────────────────────────────────────────────────────
//
// Color-codes P95 (the user-perceived ceiling) — amber over 5s, red over 15s.
// Sorted descending by default so the slowest agents float to the top.

const RunLatencyTable = observer(function RunLatencyTable({
  rows,
  loading,
  error,
}: {
  rows: RunLatencyBucket[];
  loading: boolean;
  error: string | null;
}) {
  const columns: ColumnsType<RunLatencyBucket> = [
    {
      title: 'Agent',
      dataIndex: 'agentName',
      key: 'agent',
      render: (n: string, row) => (
        <span>
          {n ?? '—'}{' '}
          {row.agentId != null && (
            <Text type="secondary" style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>
              #{row.agentId}
            </Text>
          )}
        </span>
      ),
    },
    {
      title: 'Model',
      dataIndex: 'modelName',
      key: 'model',
      render: (n: string) => <Text style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5 }}>{n ?? '—'}</Text>,
    },
    { title: 'Calls', dataIndex: 'callCount', key: 'calls', width: 80, align: 'right' },
    { title: 'P50', dataIndex: 'p50Ms', key: 'p50', width: 110, align: 'right',
      render: (n: number) => `${n.toLocaleString()} ms` },
    {
      title: 'P95',
      dataIndex: 'p95Ms',
      key: 'p95',
      width: 110,
      align: 'right',
      sorter: (a, b) => a.p95Ms - b.p95Ms,
      defaultSortOrder: 'descend' as const,
      render: (n: number) => {
        const tone = n > 15000 ? colors.red[600] : n > 5000 ? colors.amber[600] : 'inherit';
        return <span style={{ color: tone, fontWeight: tone === 'inherit' ? 400 : 600 }}>{n.toLocaleString()} ms</span>;
      },
    },
    { title: 'P99', dataIndex: 'p99Ms', key: 'p99', width: 110, align: 'right',
      render: (n: number) => `${n.toLocaleString()} ms` },
    { title: 'Max', dataIndex: 'maxMs', key: 'max', width: 110, align: 'right',
      render: (n: number) => `${n.toLocaleString()} ms` },
  ];
  return <DataTable rows={rows} columns={columns} loading={loading} error={error} rowKey={(r) => `${r.agentId}-${r.modelName}`} />;
});

// ── 9. Error mix table ──────────────────────────────────────────────────────
//
// Each row is one (agent, error_kind) bucket. ErrorKind chips use semantic
// color so rate_limit_429 reads amber and content_filter_503 reads red.

const ErrorMixTable = observer(function ErrorMixTable({
  rows,
  loading,
  error,
}: {
  rows: ErrorMixBucket[];
  loading: boolean;
  error: string | null;
}) {
  const columns: ColumnsType<ErrorMixBucket> = [
    {
      title: 'Agent',
      dataIndex: 'agentName',
      key: 'agent',
      render: (n: string, row) => n ?? (row.agentId != null ? `#${row.agentId}` : '—'),
    },
    {
      title: 'Error kind',
      dataIndex: 'errorKind',
      key: 'kind',
      render: (kind: string) => {
        // Coarse semantic mapping. Anything we don't recognize falls through
        // to slate so the chart legend stays readable.
        const tone =
          kind.includes('content_filter') || kind.includes('5xx') || kind.includes('outputs_invalid')
            ? 'red'
            : kind.includes('429') || kind.includes('rate_limit') || kind.includes('timeout')
              ? 'amber'
              : 'slate';
        return <StatusPill tone={tone}>{kind}</StatusPill>;
      },
    },
    {
      title: 'Count',
      dataIndex: 'count',
      key: 'count',
      width: 100,
      align: 'right',
      sorter: (a, b) => a.count - b.count,
      defaultSortOrder: 'descend' as const,
      render: (n: number) => <Text strong>{n.toLocaleString()}</Text>,
    },
    {
      title: 'Last seen',
      dataIndex: 'lastSeenUtc',
      key: 'lastSeen',
      width: 200,
      render: (iso: string) => new Date(iso).toLocaleString(),
    },
  ];
  return <DataTable rows={rows} columns={columns} loading={loading} error={error} rowKey={(r) => `${r.agentId}-${r.errorKind}`} />;
});

// ── 10. Tool performance table ──────────────────────────────────────────────

const ToolPerformanceTable = observer(function ToolPerformanceTable({
  rows,
  loading,
  error,
}: {
  rows: ToolPerformanceBucket[];
  loading: boolean;
  error: string | null;
}) {
  const columns: ColumnsType<ToolPerformanceBucket> = [
    {
      title: 'Plugin',
      dataIndex: 'pluginName',
      key: 'plugin',
      render: (n: string) => <Text style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5 }}>{n}</Text>,
    },
    {
      title: 'Method',
      dataIndex: 'methodName',
      key: 'method',
      render: (n: string) => <Text style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5 }}>{n}</Text>,
    },
    { title: 'Calls', dataIndex: 'callCount', key: 'calls', width: 80, align: 'right' },
    { title: 'P50', dataIndex: 'p50Ms', key: 'p50', width: 100, align: 'right',
      render: (n: number) => `${n.toLocaleString()} ms` },
    {
      title: 'P95',
      dataIndex: 'p95Ms',
      key: 'p95',
      width: 110,
      align: 'right',
      sorter: (a, b) => a.p95Ms - b.p95Ms,
      defaultSortOrder: 'descend' as const,
      render: (n: number) => {
        const tone = n > 5000 ? colors.red[600] : n > 2000 ? colors.amber[600] : 'inherit';
        return <span style={{ color: tone, fontWeight: tone === 'inherit' ? 400 : 600 }}>{n.toLocaleString()} ms</span>;
      },
    },
    { title: 'P99', dataIndex: 'p99Ms', key: 'p99', width: 100, align: 'right',
      render: (n: number) => `${n.toLocaleString()} ms` },
    { title: 'Errors', dataIndex: 'errorCount', key: 'errors', width: 90, align: 'right',
      render: (n: number) => (n > 0 ? <span style={{ color: colors.red[600] }}>{n}</span> : 0) },
    {
      title: 'Error %',
      dataIndex: 'errorRatePct',
      key: 'errorPct',
      width: 110,
      align: 'right',
      render: (n: number) => {
        const tone = n >= 5 ? colors.red[600] : n >= 1 ? colors.amber[600] : 'inherit';
        return <span style={{ color: tone, fontWeight: tone === 'inherit' ? 400 : 600 }}>{Number(n).toFixed(2)} %</span>;
      },
    },
  ];
  return <DataTable rows={rows} columns={columns} loading={loading} error={error} rowKey={(r) => `${r.pluginName}.${r.methodName}`} />;
});

// ── 11. Thread context utilization table ────────────────────────────────────

const ThreadContextTable = observer(function ThreadContextTable({
  rows,
  loading,
  error,
}: {
  rows: ThreadContextBucket[];
  loading: boolean;
  error: string | null;
}) {
  const columns: ColumnsType<ThreadContextBucket> = [
    {
      title: 'Thread',
      dataIndex: 'threadId',
      key: 'thread',
      render: (id: string) => (
        <Text style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>
          {id.length > 32 ? `${id.slice(0, 28)}…` : id}
        </Text>
      ),
    },
    {
      title: 'Agent',
      dataIndex: 'agentName',
      key: 'agent',
      render: (n: string, row) => n ?? (row.agentId != null ? `#${row.agentId}` : '—'),
    },
    { title: 'Calls', dataIndex: 'callCount', key: 'calls', width: 80, align: 'right' },
    {
      title: 'Avg ctx %',
      dataIndex: 'avgPercentOfContext',
      key: 'avgCtx',
      width: 110,
      align: 'right',
      render: (n: number | null) => (n == null ? '—' : `${Number(n).toFixed(1)} %`),
    },
    {
      title: 'Max ctx %',
      dataIndex: 'maxPercentOfContext',
      key: 'maxCtx',
      width: 130,
      align: 'right',
      sorter: (a, b) => Number(a.maxPercentOfContext ?? 0) - Number(b.maxPercentOfContext ?? 0),
      defaultSortOrder: 'descend' as const,
      render: (n: number | null) => {
        if (n == null) return '—';
        const v = Number(n);
        const tone = v >= 95 ? colors.red[600] : v >= 80 ? colors.amber[600] : 'inherit';
        return <span style={{ color: tone, fontWeight: tone === 'inherit' ? 400 : 600 }}>{v.toFixed(1)} %</span>;
      },
    },
    {
      title: 'Last seen',
      dataIndex: 'lastSeenUtc',
      key: 'lastSeen',
      width: 200,
      render: (iso: string) => new Date(iso).toLocaleString(),
    },
  ];
  return <DataTable rows={rows} columns={columns} loading={loading} error={error} rowKey={(r) => r.threadId} />;
});

// ── 12. Conversation cost panel ─────────────────────────────────────────────
//
// Combines a stats strip (median / P95 / P99 / max + total) with a top-N
// table. Stats give the long-tail context; table names the outliers.

const ConversationCostPanel = observer(function ConversationCostPanel({
  report,
  loading,
  error,
}: {
  report: ConversationCostReport | null;
  loading: boolean;
  error: string | null;
}) {
  if (loading && report == null) return <Skeleton active paragraph={{ rows: 4 }} />;
  if (error) return <Alert type="error" message={error} showIcon closable />;
  if (!report || report.top.length === 0) return <BrandedEmpty title="No conversation cost data in this window" size="sm" />;

  const stats = report.stats;
  const formatUsd = (n: number) => `$${Number(n).toFixed(4)}`;

  const columns: ColumnsType<ConversationCostBucket> = [
    {
      title: 'Thread',
      dataIndex: 'threadId',
      key: 'thread',
      render: (id: string) => (
        <Text style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12 }}>
          {id.length > 32 ? `${id.slice(0, 28)}…` : id}
        </Text>
      ),
    },
    {
      title: 'Agent',
      dataIndex: 'agentName',
      key: 'agent',
      render: (n: string, row) => n ?? (row.agentId != null ? `#${row.agentId}` : '—'),
    },
    { title: 'Calls', dataIndex: 'callCount', key: 'calls', width: 80, align: 'right' },
    {
      title: 'Tokens',
      dataIndex: 'totalTokens',
      key: 'tokens',
      width: 130,
      align: 'right',
      render: (n: number) => Number(n).toLocaleString(),
    },
    {
      title: 'Cost',
      dataIndex: 'estimatedCostUsd',
      key: 'cost',
      width: 130,
      align: 'right',
      sorter: (a, b) => Number(a.estimatedCostUsd) - Number(b.estimatedCostUsd),
      defaultSortOrder: 'descend' as const,
      render: (n: number, row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Text strong>{formatUsd(n)}</Text>
          {row.callsMissingCost > 0 && (
            <Tooltip title={`At least this much: ${row.callsMissingCost} of ${row.callCount} calls in this conversation have no cost.`}>
              <WarningOutlined style={{ color: colors.amber[600] }} aria-label="cost is a floor" />
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: 'Last seen',
      dataIndex: 'lastSeenUtc',
      key: 'lastSeen',
      width: 200,
      render: (iso: string) => new Date(iso).toLocaleString(),
    },
  ];

  return (
    <Space direction="vertical" size={14} style={{ width: '100%' }}>
      <Row gutter={[12, 12]}>
        <Col xs={12} md={4}>
          <KpiTile label="Conversations" value={stats.conversationCount.toLocaleString()} hint="In window" accent="violet" />
        </Col>
        <Col xs={12} md={4}>
          <KpiTile
            label="Total cost"
            value={`${stats.conversationsMissingCost > 0 ? '≥ ' : ''}${formatUsd(stats.totalCostUsd)}`}
            hint={stats.conversationsMissingCost > 0 ? `${stats.conversationsMissingCost} conversations include calls with no cost` : 'Across all threads'}
            accent="amber"
          />
        </Col>
        <Col xs={12} md={4}>
          <KpiTile label="Median" value={formatUsd(stats.medianCostUsd)} hint="P50 per conversation" accent="green" />
        </Col>
        <Col xs={12} md={4}>
          <KpiTile label="P95" value={formatUsd(stats.p95CostUsd)} hint="95th percentile" accent="amber" />
        </Col>
        <Col xs={12} md={4}>
          <KpiTile label="P99" value={formatUsd(stats.p99CostUsd)} hint="99th percentile" accent="red" />
        </Col>
        <Col xs={12} md={4}>
          <KpiTile label="Max" value={formatUsd(stats.maxCostUsd)} hint="Most expensive thread" accent="red" />
        </Col>
      </Row>
      <DataTable rows={report.top} columns={columns} loading={false} error={null} rowKey={(r) => r.threadId} />
    </Space>
  );
});

// ── Shared table wrapper ────────────────────────────────────────────────────

function DataTable<T extends object>({
  rows,
  columns,
  loading,
  error,
  rowKey,
}: {
  rows: T[];
  columns: ColumnsType<T>;
  loading: boolean;
  error: string | null;
  rowKey: (row: T) => string;
}) {
  if (loading && rows.length === 0) return <Skeleton active paragraph={{ rows: 4 }} />;
  if (error) return <Alert type="error" message={error} showIcon closable />;
  if (rows.length === 0) return <BrandedEmpty title="No data in this window" size="sm" />;

  return (
    <Table<T>
      columns={columns}
      dataSource={rows}
      rowKey={rowKey}
      pagination={rows.length > 10 ? { pageSize: 10, showSizeChanger: true } : false}
      size="small"
      scroll={{ x: 'max-content' }}
    />
  );
}

// ── Small KPI stat for sub-sections ─────────────────────────────────────────

const SmallStat = observer(function SmallStat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent: 'green' | 'amber' | 'red' | 'ink' | 'violet';
}) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const fg =
    accent === 'green' ? colors.green[600]
      : accent === 'amber' ? colors.amber[600]
      : accent === 'red' ? colors.red[600]
      : accent === 'violet' ? colors.violet[600]
      : colors.ink[600];

  return (
    <div
      style={{
        background: isDark ? colors.slate[800] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderRadius: 10,
        padding: '10px 14px',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: isDark ? colors.slate[400] : colors.slate[500],
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color: fg, fontFamily: "'JetBrains Mono', monospace" }}>
        {value}
      </div>
    </div>
  );
});
